import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';

import {
    ArrowLeftIcon,
    TrashIcon,
    TagIcon,
    SaveIcon,
    XIcon,
} from '@heroicons/react/outline';

import {
    useDeleteCategoryMutation,
    useEditCategoryMutation,
    useGetCategoryByIdQuery,
} from '../../Features/categories';

function EditCategoryView() {
    const navigate = useNavigate();
    const { category_id } = useParams();
    const { isOnline } = useIsOnline();

    const [progress, setProgress] = useState(0);

    const [errors, setErrors] = useState({
        name: '',
        description: '',
    });

    const [input, setInput] = useState({
        name: '',
        description: '',
    });

    const [editCategory, { isLoading: isLoadingEditCategory }] =
        useEditCategoryMutation();

    const [deleteCategory, { isLoading: isLoadingDeleteCategory }] =
        useDeleteCategoryMutation();

    const {
        data: categories,
        isLoading,
        isSuccess,
    } = useGetCategoryByIdQuery(category_id);

    useEffect(() => {
        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess) {
            setProgress(100);

            if (categories?.hasOwnProperty('data')) {
                setInput({
                    ...categories.data,
                    name: categories.data.name || '',
                    description: categories.data.description || '',
                });
            }
        }
    }, [categories, isLoading, isSuccess]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleDelete = async (e, id) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again'
            );
        }

        if (!id) {
            return;
        }

        if (
            window.confirm(
                'Are you sure you want to delete this category? This action cannot be undone.'
            )
        ) {
            try {
                setProgress(80);

                const resp = await deleteCategory(id).unwrap();

                if (resp?.hasOwnProperty('success')) {
                    setProgress(100);
                    navigate(-1);
                }
            } catch (err) {
                setProgress(100);

                swal(
                    'Error',
                    err?.data?.message ||
                        err?.data?.error ||
                        err?.message ||
                        'Something went wrong while deleting the category.',
                    'error'
                );
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again'
            );
        }

        setErrors({
            name: '',
            description: '',
        });

        try {
            setProgress(80);

            const resp = await editCategory(input).unwrap();

            if (resp?.hasOwnProperty('success')) {
                setProgress(100);
                navigate(-1);
            }
        } catch (err) {
            setProgress(100);

            if (err?.data?.hasOwnProperty('errors')) {
                setErrors({
                    name: err.data.errors?.name || '',
                    description: err.data.errors?.description || '',
                });
            } else if (err?.data?.hasOwnProperty('error')) {
                setInput((prev) => ({
                    ...prev,
                    errorMassage: err.data.error,
                }));
            } else {
                swal(
                    'Error',
                    err?.data?.message ||
                        err?.message ||
                        'Something went wrong while updating the category.',
                    'error'
                );
            }
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again'
            );
        }

        navigate(-1);
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">
            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            {/* Header */}
            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                            <TagIcon className="w-6 h-6 text-[#80b643]" />
                        </div>

                        <div>
                            <h1 className="text-lg sm:text-xl font-semibold text-gray-800 dark:text-white">
                                Edit Category
                            </h1>

                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Update category information and manage this
                                category
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={back}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:border-[#80b643] hover:text-[#80b643] transition"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Back
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="w-full px-4 sm:px-6 lg:px-8 mt-6">
                <div className="max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit}>
                        {/* Category Information */}
                        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
                            <div className="px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                        <TagIcon className="w-5 h-5 text-[#80b643]" />
                                    </div>

                                    <div>
                                        <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
                                            Category Information
                                        </h2>

                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            Update the category details below
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="p-5 sm:p-6 space-y-6">
                                {/* Name */}
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                    >
                                        Category Name
                                    </label>

                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={input.name || ''}
                                        onChange={handleChange}
                                        placeholder="Enter category name"
                                        className={`w-full px-4 py-3 rounded-lg border ${
                                            errors.name
                                                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                                : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]/20'
                                        } bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white placeholder-gray-400 outline-none focus:ring-2 transition`}
                                    />

                                    {errors.name && (
                                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>

                                {/* Description */}
                                <div>
                                    <label
                                        htmlFor="description"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                    >
                                        Description
                                        <span className="text-gray-400 font-normal ml-1">
                                            (Optional)
                                        </span>
                                    </label>

                                    <textarea
                                        name="description"
                                        id="description"
                                        rows="5"
                                        value={input.description || ''}
                                        onChange={handleChange}
                                        placeholder="Enter category description"
                                        className={`w-full px-4 py-3 rounded-lg border ${
                                            errors.description
                                                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                                                : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]/20'
                                        } bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white placeholder-gray-400 outline-none focus:ring-2 transition resize-none`}
                                    />

                                    {errors.description && (
                                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                            {errors.description}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Delete Category */}
                        <div className="mt-5 bg-white dark:bg-gray-900 border border-red-100 dark:border-red-900/40 rounded-xl shadow-sm overflow-hidden">
                            <div className="p-5 sm:p-6">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                                            <TrashIcon className="w-5 h-5 text-red-500" />
                                        </div>

                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                                                Delete Category
                                            </h3>

                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                Permanently delete this category
                                                from the system.
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={(e) =>
                                            handleDelete(e, input.id)
                                        }
                                        disabled={isLoadingDeleteCategory}
                                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <TrashIcon className="w-4 h-4" />

                                        {isLoadingDeleteCategory
                                            ? 'Deleting...'
                                            : 'Delete Category'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="mt-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm p-4 sm:p-5">
                            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                                <button
                                    type="button"
                                    onClick={back}
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                                >
                                    <XIcon className="w-4 h-4" />
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={isLoadingEditCategory}
                                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium border border-[#80b643] shadow-sm transition disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    <SaveIcon className="w-4 h-4" />

                                    {isLoadingEditCategory
                                        ? 'Saving...'
                                        : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditCategoryView;